import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShoppingBag, Tag, Plus, Minus, ArrowRight } from "@phosphor-icons/react";
import { useCart } from "../../lib/useCart";
import { api } from "../../lib/api";
import { useCurrency } from "../../lib/currency";

/** Slide-in cart drawer triggered from the header (or any page via the
 * `md-cart-open` custom event). Owns coupon application UX too. */
export default function CartDrawer({ open, onClose }) {
  const { cart, count: cartCount, total: cartTotal, add: addCart, dec: decCart, remove: removeCart } = useCart();
  const { format } = useCurrency();
  const [coupons, setCoupons] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/coupons/public").then((r) => setCoupons(r.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open) document.body.classList.add("cart-open");
    else document.body.classList.remove("cart-open");
    return () => document.body.classList.remove("cart-open");
  }, [open]);

  const discount = appliedCoupon
    ? Math.max(0, Math.round((appliedCoupon.discount_amount || 0) + (cartTotal * (appliedCoupon.discount_percent || 0) / 100)))
    : 0;

  const applyCoupon = () => {
    const code = (couponInput || "").trim().toUpperCase();
    if (!code) return;
    const match = coupons.find((c) => c.code === code);
    if (match) setAppliedCoupon(match);
    else setAppliedCoupon({ code, description: "Not found", discount_amount: 0, discount_percent: 0, _error: true });
  };

  if (!open) return null;

  return (
    <div className="md-cart-drawer-overlay" onClick={onClose} data-testid="cart-drawer">
      <aside className="md-cart-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="md-cart-drawer-head">
          <h3 className="md-cart-drawer-title">Shopping cart <span className="md-cart-drawer-count">{cartCount}</span></h3>
          <button onClick={onClose} className="md-cart-drawer-close" aria-label="Close cart"><X size={18} /></button>
        </header>
        <div className="md-cart-drawer-body">
          {cart.length === 0 ? (
            <div className="md-cart-drawer-empty">
              <ShoppingBag size={36} weight="duotone" />
              <p>Your bag is empty.</p>
              <Link to="/shop" onClick={onClose} className="btn-outline text-sm" data-testid="cart-go-shop">Browse the Shop</Link>
            </div>
          ) : (
            cart.map((c) => (
              <div key={c.id} className="md-cart-row" data-testid={`cart-row-${c.id}`}>
                <div className="md-cart-row-thumb">
                  {c.image_url ? <img src={c.image_url} alt={c.name} /> : <ShoppingBag size={22} weight="duotone" />}
                </div>
                <div className="md-cart-row-meta">
                  <div className="md-cart-row-name">{c.name}</div>
                  <div className="md-cart-row-qty">
                    <button onClick={() => decCart(c.id)} aria-label="Decrease"><Minus size={11} weight="bold" /></button>
                    <span>{c.qty}</span>
                    <button onClick={() => addCart(c)} aria-label="Increase"><Plus size={11} weight="bold" /></button>
                  </div>
                </div>
                <div className="md-cart-row-side">
                  <div className="md-cart-row-price">{format(c.price * c.qty)}</div>
                  <button onClick={() => removeCart(c.id)} className="md-cart-row-close" aria-label="Remove item" data-testid={`cart-remove-${c.id}`}>
                    <X size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        {cart.length > 0 && (
          <footer className="md-cart-drawer-foot">
            <div className="md-cart-coupon">
              <Tag size={14} weight="bold" />
              <input
                placeholder="Enter coupon code"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                data-testid="cart-coupon-input"
              />
              <button onClick={applyCoupon} data-testid="cart-coupon-apply">Apply</button>
            </div>
            {appliedCoupon && (
              <div className="md-cart-coupon-applied" data-testid="cart-coupon-applied">
                <span><strong>{appliedCoupon.code}</strong> applied · −{format(discount)}</span>
                <button onClick={() => { setAppliedCoupon(null); setCouponInput(""); }} aria-label="Remove coupon"><X size={11} /></button>
              </div>
            )}
            <div className="md-cart-drawer-totals">
              <div className="md-cart-drawer-row-sub">
                <span>Subtotal</span><span>{format(cartTotal)}</span>
              </div>
              {discount > 0 && (
                <div className="md-cart-drawer-row-sub md-cart-drawer-row-disc">
                  <span>Discount</span><span>−{format(discount)}</span>
                </div>
              )}
              <div className="md-cart-drawer-total">
                <span>Total</span><span>{format(Math.max(0, cartTotal - discount))}</span>
              </div>
            </div>
            <div className="md-cart-drawer-actions">
              <Link to="/cart" onClick={onClose} className="btn-outline text-sm justify-center" data-testid="cart-view-cart">View Cart</Link>
              <Link to="/checkout" onClick={onClose} className="btn-primary text-sm justify-center" data-testid="cart-checkout">
                Checkout <ArrowRight size={13} weight="bold" />
              </Link>
            </div>
            <div className="md-cart-trust" data-testid="cart-trust-strip">
              <span>✓ Secure Razorpay payments</span>
              <span>✓ Instant digital delivery</span>
              <span>✓ 7-day money-back guarantee</span>
            </div>
            <p className="md-cart-drawer-note">Taxes calculated at checkout · all products are digital.</p>
          </footer>
        )}
      </aside>
    </div>
  );
}
